import * as THREE from 'three';
import { getFindingClassKey, getFindingMediaColor } from './findings';
import {
  getFindingDisplayRotationX,
  getFindingDisplayRotationY,
  getFindingDisplayRotationZ,
  getFindingTransform,
  transformPosition,
} from './meshTransforms';

const TEXTURE_SIZE = 128;
const TEXTURE_CENTER = TEXTURE_SIZE / 2;
const OUTER_RADIUS = 48;
const INNER_RADIUS = 32;
const INNER_STROKE_WIDTH = 3.2;
const HIT_TARGET_RADIUS = 22;
const SPRITE_SIZE_PX = 40;
const SURFACE_CLEARANCE = 3;

export const FINDING_HIT_TARGET_SIZE = HIT_TARGET_RADIUS * 2;

const worldPosition = new THREE.Vector3();
const cameraWorldPosition = new THREE.Vector3();
const surfaceWorldPosition = new THREE.Vector3();
const liftedWorldPosition = new THREE.Vector3();
const projectedPosition = new THREE.Vector3();
const findingGroupWorldInverse = new THREE.Matrix4();

export const getFindingKey = (finding) =>
  `${finding.ownerMeshType}-${finding.id}`;

const createFindingTexture = (finding) => {
  const canvas = document.createElement('canvas');
  canvas.width = TEXTURE_SIZE;
  canvas.height = TEXTURE_SIZE;

  const context = canvas.getContext('2d');
  const markerColor = getFindingMediaColor(finding);

  context.beginPath();
  context.arc(TEXTURE_CENTER, TEXTURE_CENTER, OUTER_RADIUS, 0, Math.PI * 2);
  context.fillStyle = markerColor;
  context.fill();

  context.beginPath();
  context.arc(TEXTURE_CENTER, TEXTURE_CENTER, INNER_RADIUS, 0, Math.PI * 2);
  context.fillStyle = markerColor;
  context.fill();
  context.lineWidth = INNER_STROKE_WIDTH;
  context.strokeStyle = '#ffffff';
  context.stroke();

  if (getFindingClassKey(finding) !== 'tartar') {
    context.fillStyle = '#ffffff';
    context.font = "bold 38px 'Source Sans Pro', Arial, sans-serif";
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(String(finding.id), TEXTURE_CENTER, TEXTURE_CENTER + 1);
  }

  return new THREE.CanvasTexture(canvas);
};

export const clearFindingMarkers = (findingGroup) => {
  findingGroup.children.slice().forEach((marker) => {
    findingGroup.remove(marker);
    marker.material.map?.dispose();
    marker.material.dispose();
  });
};

export const createFindingMarkers = (
  findingGroup,
  findings,
  meshCenter,
  meshTransforms = {},
  displayOffsets = {},
) => {
  findings.forEach((finding) => {
    const material = new THREE.SpriteMaterial({
      map: createFindingTexture(finding),
      depthTest: true,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -4,
      sizeAttenuation: false,
      transparent: true,
    });
    const marker = new THREE.Sprite(material);
    const position =
      finding.positionSpace === 'view'
        ? finding.position.map(Number)
        : transformPosition(
            finding.position,
            getFindingTransform(meshTransforms, finding.ownerMeshType),
          );
    const displayPosition = new THREE.Vector3(
      position[0] - meshCenter.x,
      position[1] - meshCenter.y,
      position[2] - meshCenter.z,
    ).applyEuler(
      new THREE.Euler(
        finding.positionSpace === 'view'
          ? getFindingDisplayRotationX(finding.ownerMeshType)
          : Math.PI / 2,
        finding.positionSpace === 'view'
          ? getFindingDisplayRotationY(finding.ownerMeshType)
          : Math.PI,
        finding.positionSpace === 'view'
          ? getFindingDisplayRotationZ(finding.ownerMeshType)
          : 0,
      ),
    );
    const displayOffset = displayOffsets[finding.ownerMeshType];
    if (displayOffset) displayPosition.add(new THREE.Vector3(...displayOffset));
    marker.position.copy(displayPosition);
    marker.userData.surfacePosition = displayPosition.clone();
    marker.renderOrder = 10;
    marker.userData.finding = finding;
    marker.userData.ownerMeshType = finding.ownerMeshType;
    findingGroup.add(marker);
  });
};

export const updateFindingMarkers = (findingGroup, camera, renderer, targets) => {
  const width = renderer.domElement.clientWidth;
  const height = renderer.domElement.clientHeight;
  const projectionScaleX = camera.projectionMatrix.elements[0];
  const projectionScaleY = camera.projectionMatrix.elements[5];
  if (!width || !height || !projectionScaleX || !projectionScaleY) return;

  const spriteScaleX = (SPRITE_SIZE_PX * 2) / (width * projectionScaleX);
  const spriteScaleY = (SPRITE_SIZE_PX * 2) / (height * projectionScaleY);

  camera.updateMatrixWorld();
  camera.getWorldPosition(cameraWorldPosition);
  findingGroup.updateWorldMatrix(true, true);
  findingGroupWorldInverse.copy(findingGroup.matrixWorld).invert();

  findingGroup.children.forEach((marker) => {
    marker.scale.set(spriteScaleX, spriteScaleY, 1);

    marker.position.copy(marker.userData.surfacePosition);
    marker.updateWorldMatrix(false, false);
    marker.getWorldPosition(surfaceWorldPosition);
    liftedWorldPosition
      .copy(cameraWorldPosition)
      .sub(surfaceWorldPosition)
      .normalize()
      .multiplyScalar(SURFACE_CLEARANCE)
      .add(surfaceWorldPosition)
      .applyMatrix4(findingGroupWorldInverse);
    marker.position.copy(liftedWorldPosition);
    marker.updateWorldMatrix(false, false);

    const target = targets.get(getFindingKey(marker.userData.finding));
    if (!target) return;

    marker.getWorldPosition(worldPosition);
    projectedPosition.copy(worldPosition).project(camera);
    const isVisible =
      marker.visible && projectedPosition.z >= -1 && projectedPosition.z <= 1;
    target.style.display = isVisible ? 'block' : 'none';
    if (!isVisible) return;

    const left = (projectedPosition.x * 0.5 + 0.5) * width - HIT_TARGET_RADIUS;
    const top = (-projectedPosition.y * 0.5 + 0.5) * height - HIT_TARGET_RADIUS;
    target.style.transform = `translate3d(${left}px, ${top}px, 0)`;
  });
};
